// UI compartida — extraída de App.jsx para reutilización (módulo BI y futuros)
import React from "react";
import { Download } from "lucide-react";
import { SIMBOLO_MONEDA_DEFAULT } from "./moneda";
import { fechaLocalISO } from "./fechas";

export const COLORS = {
  bg:           "#F9FAFB",
  surface:      "#FFFFFF",
  surfaceHover: "#F3F4F6",
  border:       "#E5E7EB",
  borderFocus:  "#4F46E5",
  primary:      "#4F46E5",
  primarySoft:  "#EEF2FF",
  accent:       "#6366F1",
  text:         "#111827",
  textSecond:   "#374151",
  textMuted:    "#6B7280",
  textDim:      "#9CA3AF",
  danger:       "#DC2626",
  dangerLight:  "#FCA5A5",
  dangerSoft:   "#FEF2F2",
  warning:      "#D97706",
  warningSoft:  "#FFFBEB",
  success:      "#059669",
  successSoft:  "#F0FDF4",
  ganador:      "#059669",
  normal:       "#6B7280",
  perdedor:     "#DC2626",
  chart:        ["#4F46E5", "#818CF8", "#C7D2FE", "#6366F1", "#3730A3", "#A5B4FC"],
};

export const API_BASE = "/api/v1";

export const EVENTO_SESION_EXPIRADA = "sesion-expirada";

export async function apiFetch(path, options = {}, token = null) {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  let res;
  try { res = await fetch(`${API_BASE}${path}`, { ...options, headers }); }
  catch (e) { throw new Error("No se pudo conectar con el servidor. Verifique su conexión."); }
  if (res.status === 429) throw new Error("Demasiadas solicitudes. Espere un momento.");
  // Con token, un 401 es la sesión vencida (dura 8 h): App vuelve al login.
  // Sin token es el propio login con credenciales malas y sigue el camino normal.
  if (res.status === 401 && token) {
    window.dispatchEvent(new Event(EVENTO_SESION_EXPIRADA));
    throw new Error("Su sesión expiró. Ingrese de nuevo.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = err.detail;
    if (Array.isArray(detail)) throw new Error(detail.map(d => d.msg || JSON.stringify(d)).join(". "));
    throw new Error(typeof detail === "string" ? detail : `Error ${res.status}`);
  }
  return res.json();
}

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════
export const inputStyle = {
  width: "100%", padding: "10px 14px",
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: 8, color: COLORS.text, fontSize: 14,
  fontFamily: "'DM Sans', sans-serif",
  outline: "none", boxSizing: "border-box",
};
export const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 600,
  color: "#374151", marginBottom: 6,
};
export const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "9px 16px", background: COLORS.primary,
  border: "none", borderRadius: 7, color: "white",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};
export const btnSecondary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "9px 14px", background: COLORS.surface,
  border: `1px solid ${COLORS.border}`, borderRadius: 7,
  color: "#374151", fontSize: 13, fontWeight: 500, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};
export const btnSmallSuccess = {
  padding: "4px 10px", background: COLORS.successSoft,
  border: "1px solid #A7F3D0", borderRadius: 6,
  color: COLORS.success, fontSize: 11, fontWeight: 600, cursor: "pointer",
};
export const btnSmallDanger = {
  padding: "4px 10px", background: COLORS.dangerSoft,
  border: "1px solid #FECACA", borderRadius: 6,
  color: COLORS.danger, fontSize: 11, fontWeight: 600, cursor: "pointer",
};
export const btnSmallInfo = {
  padding: "4px 10px", background: COLORS.primarySoft,
  border: "1px solid #C7D2FE", borderRadius: 6,
  color: COLORS.primary, fontSize: 11, fontWeight: 600, cursor: "pointer",
};
export const thStyle = {
  padding: "10px 16px", textAlign: "left",
  fontSize: 11, color: COLORS.textDim,
  fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px",
  background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`,
};
export const tdStyle = {
  padding: "12px 16px", fontSize: 13,
  color: COLORS.textSecond, borderBottom: `1px solid #F3F4F6`,
};
export const mono = { fontFamily: "'JetBrains Mono', monospace" };
export const cardStyle = {
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: 10, padding: 20,
};
export const tableWrap = {
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: 10, overflow: "hidden",
};

// ═══════════════════════════════════════════════════════════════
// CSV DOWNLOAD UTILITY
// ═══════════════════════════════════════════════════════════════
export function downloadCSV(data, filename) {
  if (!data || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(",")];
  for (const row of data) {
    csvRows.push(headers.map(h => {
      let val = row[h] ?? "";
      val = String(val).replace(/"/g, '""');
      return val.includes(",") || val.includes("\n") || val.includes('"') ? `"${val}"` : val;
    }).join(","));
  }
  const blob = new Blob(["\uFEFF" + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}_${fechaLocalISO()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function CSVButton({ data, filename, label }) {
  if (!data || data.length === 0) return null;
  return (
    <button onClick={() => downloadCSV(data, filename)}
      style={{ ...btnSmallSuccess, padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Download size={14} /> {label || `CSV (${data.length})`}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════
export function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
      <div>
        <h2 style={{ color: COLORS.text, fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.3px" }}>{title}</h2>
        {subtitle && <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "3px 0 0" }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
export function LoadingSpinner() {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: COLORS.textDim, fontSize: 14 }}>Cargando...</div>;
}
export function EmptyState({ message }) {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, color: COLORS.textDim, fontSize: 14 }}>{message}</div>;
}

export function MetricCard({ icon: Icon, label, value, sub, color = COLORS.primary }) {
  return (
    <div style={{ ...cardStyle, borderTop: `3px solid ${color}`, padding: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: (color === COLORS.primary || color === COLORS.accent) ? COLORS.text : color, marginTop: 6, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: COLORS.textDim, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const styles = {
    CRITICO:             { bg: "#FEF2F2", color: "#DC2626", dot: "#DC2626", label: "Crítico" },
    REABASTECER:         { bg: "#FFFBEB", color: "#D97706", dot: "#D97706", label: "Reabastecer" },
    SOBREINVENTARIO:     { bg: "#EEF2FF", color: "#4F46E5", dot: "#4F46E5", label: "Sobreinv." },
    OK:                  { bg: "#F0FDF4", color: "#059669", dot: "#059669", label: "OK" },
    ganador:             { bg: "#F0FDF4", color: "#059669", dot: "#059669", label: "Ganador" },
    normal:              { bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF", label: "Normal" },
    perdedor:            { bg: "#FEF2F2", color: "#DC2626", dot: "#DC2626", label: "Perdedor" },
    sugerido:            { bg: "#FFFBEB", color: "#D97706", dot: "#D97706", label: "Sugerido" },
    con_errores:         { bg: "#FFFBEB", color: "#D97706", dot: "#D97706", label: "Con errores" },
    aprobado:            { bg: "#EEF2FF", color: "#4F46E5", dot: "#4F46E5", label: "Aprobado" },
    en_transito:         { bg: "#EEF2FF", color: "#4F46E5", dot: "#4F46E5", label: "En tránsito" },
    recibido:            { bg: "#F0FDF4", color: "#059669", dot: "#059669", label: "Recibido" },
    recibido_por_tiempo: { bg: "#FFFBEB", color: "#D97706", dot: "#D97706", label: "Recibido (tiempo)" },
    cancelado:           { bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF", label: "Cancelado" },
    exitoso:             { bg: "#F0FDF4", color: "#059669", dot: "#059669", label: "Exitoso" },
    error_reenviar:      { bg: "#FEF2F2", color: "#DC2626", dot: "#DC2626", label: "Error" },
  };
  const s = styles[status] || { bg: "#F3F4F6", color: "#6B7280", dot: "#9CA3AF", label: status };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color, fontSize: 11, fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, display: "inline-block", flexShrink: 0 }} />
      {s.label}
    </span>
  );
}


// ── Símbolo de moneda (empresas.simbolo_moneda) ────────────────────────
// Un solo GET /empresa al montar la app; las pantallas lo leen del
// contexto en vez de escribir "$" a mano. Los 4 roles tienen el permiso
// empresas:leer, pero si la llamada falla queda el default y nada se rompe.
const MonedaContext = React.createContext({
  simbolo: SIMBOLO_MONEDA_DEFAULT,
  setSimbolo: () => {},
});

export function MonedaProvider({ token, children }) {
  const [simbolo, setSimbolo] = React.useState(SIMBOLO_MONEDA_DEFAULT);
  React.useEffect(() => {
    if (!token) return undefined;
    let vivo = true;
    apiFetch("/empresa", {}, token)
      .then(e => { if (vivo && e?.simbolo_moneda) setSimbolo(e.simbolo_moneda); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [token]);
  const valor = React.useMemo(() => ({ simbolo, setSimbolo }), [simbolo]);
  return <MonedaContext.Provider value={valor}>{children}</MonedaContext.Provider>;
}

// Devuelve { simbolo, setSimbolo }; setSimbolo lo usa Configuración al guardar.
export function useMoneda() {
  return React.useContext(MonedaContext);
}
